export const countryFlags = {
  Greece: "🇬🇷",
  Croatia: "🇭🇷",
  Italy: "🇮🇹",
  Spain: "🇪🇸",
  Turkey: "🇹🇷",
  France: "🇫🇷",
  Cyprus: "🇨🇾",
  Israel: "🇮🇱",
  Montenegro: "🇲🇪",
  Malta: "🇲🇹",
  Bahamas: "🇧🇸",
  "British Virgin Islands": "🇻🇬",
  Egypt: "🇪🇬",
};

export const primaryServices = [
  "fuel",
  "electricity",
  "water",
  "wifi",
  "showers",
  "laundry",
  "restaurant",
  "supermarket",
  "repair",
  "boatyard",
  "customs",
];

export const experienceFilters = ["family", "luxury", "budget", "liveaboard"];

export function slugify(value = "") {
  return String(value).toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function normalize(value = "") {
  return String(value).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function editDistanceOne(a, b) {
  if (!a || !b || Math.abs(a.length - b.length) > 1) return false;
  let misses = 0;
  for (let i = 0, j = 0; i < a.length || j < b.length;) {
    if (a[i] === b[j]) { i += 1; j += 1; continue; }
    misses += 1;
    if (misses > 1) return false;
    if (a.length > b.length) i += 1;
    else if (b.length > a.length) j += 1;
    else { i += 1; j += 1; }
  }
  return true;
}

export function enrichMarina(marina) {
  const parts = String(marina.location || "").split(",").map((part) => part.trim()).filter(Boolean);
  const city = marina.city || parts[0] || "Coastal area";
  const country = marina.country || parts[parts.length - 1] || "International";
  const region = marina.region || marina.island || city;
  const services = (marina.facilities || []).map((item) => normalize(item).replace(/\s+/g, "_"));

  return {
    ...marina,
    city,
    country,
    region,
    services,
    slug: marina.slug || slugify(marina.name),
    flag: countryFlags[country] || "⚓",
    searchableText: normalize([
      marina.name,
      marina.location,
      city,
      country,
      region,
      marina.island,
      marina.coastline,
      marina.description,
      ...(marina.facilities || []),
      ...(marina.mooring_types || []),
      ...(marina.experience_tags || []),
    ].filter(Boolean).join(" ")),
  };
}

export function smartMatches(marina, query) {
  const q = normalize(query).trim();
  if (!q) return true;
  if (marina.searchableText.includes(q)) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  const words = marina.searchableText.split(/\s+/).filter(Boolean);
  return tokens.every((token) => words.some((word) => word.includes(token) || (token.length > 3 && editDistanceOne(token, word.slice(0, token.length)))));
}

export function buildMarinaRegionIndex(marinas = []) {
  const allRegions = new Set();
  const byCountry = {};

  marinas.forEach((marina) => {
    const country = marina.country || String(marina.location || "").split(",").map((part) => part.trim()).filter(Boolean).pop();
    if (!country) return;

    const regions = [marina.region, marina.city, marina.island, marina.coastline]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    byCountry[country] ||= new Set();
    regions.forEach((region) => {
      allRegions.add(region);
      byCountry[country].add(region);
    });
  });

  return {
    all: [...allRegions].sort(),
    byCountry: Object.fromEntries(Object.entries(byCountry).map(([country, regions]) => [country, [...regions].sort()])),
  };
}

export function filterMarinas(marinas, filters) {
  return marinas.filter((marina) => {
    if (!smartMatches(marina, filters.query)) return false;
    if (filters.country !== "all" && marina.country !== filters.country) return false;
    if (filters.region !== "all" && marina.region !== filters.region && marina.city !== filters.region && marina.island !== filters.region) return false;
    if (filters.services.length && !filters.services.every((service) => marina.services.includes(service))) return false;
    if (filters.capability === "catamaran" && !marina.catamaran_friendly) return false;
    if (filters.capability === "superyacht" && !marina.superyacht_friendly) return false;
    if (filters.capability === "sheltered" && !marina.sheltered_from_wind) return false;
    if (filters.experience !== "all" && !(marina.experience_tags || []).map(normalize).includes(filters.experience)) return false;
    return true;
  });
}

export function getCountryStats(marinas) {
  return Object.values(marinas.reduce((acc, marina) => {
    const key = marina.country;
    acc[key] ||= { country: key, flag: marina.flag, count: 0, regions: new Set(), featured: marina };
    acc[key].count += 1;
    acc[key].regions.add(marina.region);
    if (Number(marina.rating || 0) > Number(acc[key].featured?.rating || 0)) acc[key].featured = marina;
    return acc;
  }, {})).map((item) => ({ ...item, regions: [...item.regions].slice(0, 4) })).sort((a, b) => b.count - a.count);
}

export function getMapPosition(marina, marinas) {
  const valid = marinas.filter((item) => Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude)));
  const lats = valid.map((item) => Number(item.latitude));
  const lngs = valid.map((item) => Number(item.longitude));
  const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const x = ((Number(marina.longitude) - minLng) / Math.max(maxLng - minLng, 0.01)) * 76 + 12;
  const y = (1 - ((Number(marina.latitude) - minLat) / Math.max(maxLat - minLat, 0.01))) * 70 + 14;
  return { x: Math.max(8, Math.min(92, x)), y: Math.max(10, Math.min(88, y)) };
}